import { useCallback, useEffect, useMemo, useState } from "react";

import { UnfoldLessIcon, UnfoldMoreIcon } from "icons";
import { colors } from "theme/palette";

import ClickAwayListener from "components/ClickAwayListener";
import Stack from "components/Stack";
import { TableCell } from "components/Table";
import Text from "components/Text";
import TextField from "components/TextField";

interface ICell {
  cellKey: string;
  value: string;
  colSpan?: number;
  editable?: boolean;
  textAlign?: "left" | "right" | "center";
  onChangeCellValue?: ({ value, key }: { value: string; key: string }) => void;
  dataTestid?: string;
  textStyle?: { [key: string]: string | number } | null;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTableCellElement>) => void;
  isCurrentlyActive?: boolean;
  manageCellActiveStatus?: ({ activate }: { activate: boolean }) => void;
}

const Cell = ({
  cellKey: key,
  value,
  editable,
  colSpan,
  textAlign = "left",
  onChangeCellValue,
  dataTestid,
  textStyle,
  onKeyDown,
  isCurrentlyActive,
  manageCellActiveStatus,
}: ICell) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isCurrentlyActive) {
      setIsExpanded(false);
    }
  }, [isCurrentlyActive]);

  const handleClick = useCallback(() => {
    if (editable) {
      manageCellActiveStatus &&
        manageCellActiveStatus({
          activate: true,
        });
    }
  }, [editable, manageCellActiveStatus]);

  const handleSave = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeCellValue &&
        onChangeCellValue({ value: event.target.value, key });
    },
    [onChangeCellValue, key]
  );

  const onTabClick = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      if (event.key === "Tab") {
        event.preventDefault();
        onKeyDown && onKeyDown(event);
      }
    },
    [onKeyDown]
  );

  const UnfoldIcon = isExpanded ? UnfoldLessIcon : UnfoldMoreIcon;

  const styles = useMemo(
    () => ({
      border: `1px double ${colors.blue40}`,
      boxShadow: isCurrentlyActive
        ? `inset 0 0 0 2px ${colors.blue100}`
        : "none",
      padding: 2,
      "@media print": { color: colors.black },
    }),
    [isCurrentlyActive]
  );

  return (
    <TableCell
      key={key}
      colSpan={colSpan}
      sx={styles}
      align={textAlign}
      onClick={handleClick}
      onKeyDown={onTabClick}
      data-testid="table-column-cell"
      data-test-id={`${key.toLowerCase().replaceAll(" ", "-")}-cell`}
    >
      {!isCurrentlyActive && (
        <Text
          whiteSpace="pre-line"
          variant="text2"
          data-testid="cell-value"
          sx={textStyle}
        >
          {value}
        </Text>
      )}
      {editable && isCurrentlyActive && (
        <ClickAwayListener
          onClickAway={() =>
            manageCellActiveStatus &&
            manageCellActiveStatus({ activate: false })
          }
        >
          <Stack sx={{ position: "relative" }}>
            <TextField
              size={isExpanded ? undefined : "small"}
              autoFocus
              defaultValue={value}
              onChange={handleSave}
              data-testid="edit-data-cell-value-field"
              multiline={isExpanded}
              sx={{
                // These styles are effecting the base MUI styles for the TextField
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiInputBase-inputMultiline": {
                  padding: 0,
                },
                "& input": {
                  padding: 0,
                },
              }}
            />
            <UnfoldIcon
              onClick={() => setIsExpanded((prevValue) => !prevValue)}
              sx={{
                position: "absolute",
                top: "-13px",
                right: "-12px",
                backgroundColor: "white",
                borderRadius: "20px",
                padding: 0.5,
                border: `1px solid ${colors.gray40}`,
                transform: "rotate(45deg)",
                "&:hover": {
                  backgroundColor: colors.blue20,
                  cursor: "pointer",
                },
              }}
            />
          </Stack>
        </ClickAwayListener>
      )}
    </TableCell>
  );
};

export default Cell;
